import { guessLanguageFromName } from '../editor/languages';

/**
 * Names a fragment about to be added.
 *
 * The first one takes the snippet's title. Most snippets hold exactly one fragment, and making
 * someone invent a second name — or type the same one twice — for what they think of as a single
 * thing is friction with nothing behind it. Later fragments are numbered, because by then the
 * snippet genuinely has several parts and the title no longer distinguishes them.
 */
export function defaultFragmentName(snippetTitle: string, existingCount: number): string {
  if (existingCount > 0) {
    return `fragment-${existingCount + 1}.txt`;
  }

  const title = snippetTitle.trim();

  // A blank title would fail the API's validation, so fall back rather than send it.
  return title.length > 0 ? title : 'fragment-1.txt';
}

/**
 * Language for a new fragment: whatever the name implies, else plain text. A title like
 * "cleanup.sql" is a strong hint; "Update statistics" is not, and guessing from it would be
 * inventing an answer.
 */
export function defaultFragmentLanguage(name: string): string {
  return guessLanguageFromName(name)?.id ?? 'text';
}

/**
 * Language for a fragment as it is being edited, from the name first and the code second.
 *
 * The order is the whole point. A file called `deploy.sh` is shell even when its body is a single
 * SQL statement in a heredoc, because somebody said so by naming it; the code is only evidence
 * when the name offers none. Returns undefined when neither says anything, so the caller keeps
 * what it had.
 *
 * Async because the signature table is a dynamic import. It is only ever consulted with an editor
 * open and a name that gave nothing away, and this module is reachable from the library page —
 * loading it eagerly put 5 kB gzipped in front of people who never open a fragment at all.
 */
export async function detectFragmentLanguage(
  name: string,
  content: string,
): Promise<string | undefined> {
  // `text` from a name is not an answer, it is the absence of one — and the name it usually comes
  // from is `fragment-2.txt`, which this app generated rather than anybody typing it. Letting that
  // count as evidence meant every fragment after the first was never detected at all.
  const named = guessLanguageFromName(name);
  if (named && named.id !== 'text') {
    return named.id;
  }

  if (content.trim().length === 0) {
    return undefined;
  }

  const { detectLanguage } = await import('../editor/detectLanguage');
  return detectLanguage(content)?.id;
}
